import {
  AutolinkAndDateDashboardFilterNotification,
  type DashboardBuilderChartWidget,
  type DashboardBuilderNonBubbleChartWidget,
  type DashboardBuilderHeaderWidget,
  type DashboardBuilderPowerBiIframeWidget,
  type DashboardBuilderNoteWidget,
  type DashboardBuilderWidget,
  DashboardErrorType,
  type DashboardFilterParameter,
  type DashboardFilterSupportedField,
  isAllowedPowerBiUrl,
  isAutolinkAndDateDashboardFilterNotificationExpired,
  isReportFieldSupportedForDashboardFilters,
  type MinimalFieldRepresentation,
  WidgetError,
  widgetOperations,
  type WidgetValidationErrors,
  type WidgetValidationFieldErrors,
  type WidgetWarning,
} from '@ardoq/dashboard';
import {
  Aggregate,
  type APIChartWidgetAttributes,
  type APIDashboardFilterParameter,
  APIFieldType,
  type ArdoqId,
  ChartOrderOptions,
  type ConditionalFormattingLimit,
  type DashboardFilterValues,
  type DashboardSettings,
  type DataSourceField,
  DataSourceType,
  isViewpointWidgetDatasource,
  type NumberWidgetConfiguration,
  type NumberWidgetTrend,
  type NumberWidgetTrendConfiguration,
  NumberWidgetTrendRangeType,
  PersonalSetting,
  type PersonalSettingMap,
  ReportColumnType,
  type RollingDateRange,
  SortOrder,
  type SupportedDashboardFilterType,
  TrendColorScheme,
  type WidgetConfiguration,
  type WidgetDataSourceEntity,
  WidgetDataSourceTypes,
  WidgetErrorCode,
  type WidgetGlobalFilterBindings,
  WidgetTypes,
} from '@ardoq/api-types';
import { isAPIFieldTypeSortable, keepValidFilters } from '@ardoq/report-reader';
import { conditionOperations } from './sidebars/sections/conditionOperations';
import { isEmpty, isEqual, keyBy, omit, omitBy, pick } from 'lodash';
import { colors } from '@ardoq/design-tokens';
import { v4 as uuidv4 } from 'uuid';
import { isTruthy, type Maybe } from '@ardoq/common-helpers';
import { dateRangeOperations, isDateRangeFieldType } from '@ardoq/date-range';
import { IconName } from '@ardoq/icons';
import { StatusType, Tag } from '@ardoq/status-ui';
import { SURVEY_VALID_INVALID_FIELD_NAME } from './types';
import { isValidLabelFieldForCategoricalChart } from './widgetTypeValidation';
import { LABEL_FIELD_MISSING, VALUE_FIELD_MISSING } from 'dashboard/consts';
import { t, type I18nLocale } from '@ardoq/i18n';
import type { IconPropsForSelectOption, SelectOption } from '@ardoq/select';
import type { NewFeatureNotificationProps } from '@ardoq/snowflakes';
import { dispatchAction } from '@ardoq/rxbeach';
import { updatePersonalSetting } from 'streams/currentUser/actions';
import { PermissionContext } from '@ardoq/access-control';
import { toConfidentialFieldSelectOption } from './confidentialFieldsUtils';
import { fieldIsConfidential } from 'utils/confidentialFieldUtil';

const getWidgetTypeSpecificAttributes = (
  widgetAttributes: DashboardBuilderChartWidget,
  selectedFields: DataSourceField[]
) => {
  if (widgetOperations.isPieChartData(widgetAttributes)) {
    return {
      showTotal: widgetAttributes.showTotal,
      orderSelection: widgetAttributes.orderSelection,
      order:
        widgetAttributes.order ??
        getDefaultOrderSelectionDirection(widgetAttributes.orderSelection),
      isAbbreviated: widgetAttributes.isAbbreviated,
    };
  } else if (widgetOperations.isLineChartData(widgetAttributes)) {
    return {
      period: widgetAttributes.period,
      hasDynamicYAxis: widgetAttributes.hasDynamicYAxis,
      isAbbreviated: widgetAttributes.isAbbreviated,
    };
  } else if (widgetOperations.isStackedBarChartData(widgetAttributes)) {
    return {
      numberSlices: widgetAttributes.numberSlices,
      period: widgetAttributes.period,
      orderSelection: widgetAttributes.orderSelection,
      order:
        widgetAttributes.order ??
        getDefaultOrderSelectionDirection(widgetAttributes.orderSelection),
    };
  } else if (widgetOperations.isNumberWidgetData(widgetAttributes)) {
    return {
      limits: widgetAttributes.limits?.map(({ name, above, color }) => ({
        name,
        above,
        color,
      })),
      resultLabel: widgetAttributes.resultLabel,
      isAbbreviated: widgetAttributes.isAbbreviated,
      trend: widgetAttributes.trend
        ? pick(widgetAttributes.trend, ['rangeType', 'rolling', 'colorScheme'])
        : undefined,
    };
  } else if (widgetOperations.isTableWidgetData(widgetAttributes)) {
    const sortableFields = selectedFields.filter(isSortableColumInTableWidget);

    const fallBackField = sortableFields.length
      ? sortableFields[sortableFields.length - 1]
      : undefined;
    return {
      sort: widgetAttributes.sort ?? fallBackField?.name,
      order:
        widgetAttributes.order ??
        (fallBackField?.type === APIFieldType.NUMBER
          ? SortOrder.DESC
          : SortOrder.ASC),
    };
  } else if (widgetOperations.isBarChartData(widgetAttributes)) {
    return {
      colorSelection: widgetAttributes.colorSelection,
      primaryColor: widgetAttributes.primaryColor,
      secondaryColor: widgetAttributes.secondaryColor,
      orderSelection: widgetAttributes.orderSelection,
      order:
        widgetAttributes.order ??
        getDefaultOrderSelectionDirection(widgetAttributes.orderSelection),
      isAbbreviated: widgetAttributes.isAbbreviated,
    };
  } else if (widgetOperations.isPercentageChartData(widgetAttributes)) {
    return {
      condition: widgetAttributes.condition,
    };
  } else if (widgetOperations.isBubbleChartData(widgetAttributes)) {
    return {
      xAxisTitle: widgetAttributes.xAxisTitle,
      yAxisTitle: widgetAttributes.yAxisTitle,
      selectedComponentTypeIds: widgetAttributes.selectedComponentTypeIds,
      selectedComponentIds: widgetAttributes.selectedComponentIds,
      accessWorkspaceIds: widgetAttributes.accessWorkspaceIds,
      resolvedComponentColors: widgetAttributes.resolvedComponentColors,
      resolvedLabels: widgetAttributes.resolvedLabels,
      background: widgetAttributes.background,
      customQuadrantSettings: widgetAttributes.customQuadrantSettings,
      showBubblesWithZeroValue: widgetAttributes.showBubblesWithZeroValue,
      conditionalFormatting: widgetAttributes.conditionalFormatting,
      bubbleAreaScaleFactor: widgetAttributes.bubbleAreaScaleFactor,
    };
  }
  return {};
};

export const formatHeaderWidget = (
  widgetAttributes: DashboardBuilderHeaderWidget,
  isForSave: boolean = false
) => ({
  // remove id for unsaved widget when saving widget
  ...(widgetAttributes.isUnsaved && isForSave
    ? {}
    : { _id: widgetAttributes._id }),
  viewType: widgetAttributes.viewType,
  content: widgetAttributes.content,
  layout: {
    x: widgetAttributes.layout.x,
    y: widgetAttributes.layout.y,
    w: widgetAttributes.layout.w,
    h: widgetAttributes.layout.h,
    i: widgetAttributes.layout.i,
  },
});

export const formatNoteWidget = (
  widgetAttributes: DashboardBuilderNoteWidget,
  isForSave: boolean = false
) => ({
  // remove id for unsaved widget when saving widget
  ...(widgetAttributes.isUnsaved && isForSave
    ? {}
    : { _id: widgetAttributes._id }),
  viewType: widgetAttributes.viewType,
  content: widgetAttributes.content,
  layout: {
    x: widgetAttributes.layout.x,
    y: widgetAttributes.layout.y,
    w: widgetAttributes.layout.w,
    h: widgetAttributes.layout.h,
    i: widgetAttributes.layout.i,
  },
});

export const formatPowerBiIframeWidget = (
  widgetAttributes: DashboardBuilderPowerBiIframeWidget,
  isForSave: boolean = false
) => ({
  ...(widgetAttributes.isUnsaved && isForSave
    ? {}
    : { _id: widgetAttributes._id }),
  viewType: widgetAttributes.viewType,
  url: widgetAttributes.url,
  layout: {
    x: widgetAttributes.layout.x,
    y: widgetAttributes.layout.y,
    w: widgetAttributes.layout.w,
    h: widgetAttributes.layout.h,
    i: widgetAttributes.layout.i,
  },
});

const exportFieldsForSave = (
  selectedFields: DataSourceField[],
  widgetAttributes: DashboardBuilderChartWidget
) =>
  widgetOperations.isCategoricalChartData(widgetAttributes)
    ? selectedFields
        .slice(0, widgetAttributes?.aggregate === Aggregate.SUM ? 2 : 1)
        .map(field => field.name)
    : selectedFields.flatMap(field =>
        isDateRangeFieldType(field.type)
          ? [
              dateRangeOperations.toStartDateName(field.name),
              dateRangeOperations.toEndDateName(field.name),
            ]
          : field.name
      );

export const formatChartWidget = (
  widgetAttributes: DashboardBuilderChartWidget,
  availableFieldsForDataSource: DataSourceField[],
  isForSave: boolean = false
): WidgetConfiguration => {
  const layout = pick(widgetAttributes.layout, ['x', 'y', 'w', 'h', 'i']);
  const selectedFields: DataSourceField[] =
    widgetAttributes.fields
      ?.map(field =>
        availableFieldsForDataSource.find(
          availableField => availableField.name === field
        )
      )
      .filter(isTruthy) ?? [];

  const { _id, name, viewType, datasource } = widgetAttributes;

  const commonAttributes = {
    // remove id for unsaved widget when saving widget
    _id: widgetAttributes.isUnsaved && isForSave ? undefined : _id,
    layout,
    name,
    viewType,
    fields: exportFieldsForSave(selectedFields, widgetAttributes),
    numberSlices: widgetOperations.hasConfigurableSlices(widgetAttributes)
      ? widgetAttributes.numberSlices
      : undefined,
    datasource,
    aggregate:
      widgetAttributes.viewType === WidgetTypes.STACKED_BAR_CHART
        ? undefined
        : widgetAttributes.aggregate ||
          // TODO ARD-27405 Temporary until all pie/bar data migrated to have an aggregate
          widgetOperations.defaultAggregate(widgetAttributes),
    filters: widgetAttributes.filters,
    globalFilterBindings: widgetAttributes.globalFilterBindings,
  };

  const result = {
    ...commonAttributes,
    ...getWidgetTypeSpecificAttributes(widgetAttributes, selectedFields),
  } as WidgetConfiguration;

  // Skip condition filter validation when not saving a percentage widget (or when no condition)
  // since a user may be editing a condition filter which could be in an invalid state
  if (!isForSave || !('condition' in result) || !result.condition)
    return result;

  const columns = availableFieldsForDataSource.map(
    conditionOperations.dataSourceFieldToReportColumn
  );
  return {
    ...result,
    condition: keepValidFilters(result.condition, columns) ?? undefined,
  };
};

export const formatChartWidgetForApi = (
  widgetAttributes: DashboardBuilderChartWidget,
  availableFields: DataSourceField[]
): WidgetConfiguration =>
  formatChartWidget(widgetAttributes, availableFields, true);

export const createConditionalFormattingLimit =
  (): ConditionalFormattingLimit => ({
    above: 0,
    color: colors.black950,
    name: '',
    id: uuidv4(),
  });

export const isDashboardNameValid = (dashboardName: string | undefined) =>
  dashboardName && dashboardName?.trim().length > 0;

export const validateDashboard = (dashboard: { name: string | undefined }) =>
  !isDashboardNameValid(dashboard.name)
    ? new Set([DashboardErrorType.MISSING_NAME])
    : new Set([]);

export const validateWidget = (
  widget: DashboardBuilderWidget,
  availableDataSources: WidgetDataSourceEntity[],
  isLoadingDataSources: boolean
): DashboardBuilderWidget => {
  let validationProblems: WidgetValidationErrors | null = null;

  if (widgetOperations.isHeaderWidgetData(widget)) {
    if (!widgetOperations.isValidHeaderWidgetData(widget)) {
      validationProblems = {
        widgetError: WidgetError.MISSING_HEADER_TITLE,
      };
    }
  } else if (widgetOperations.isNoteWidgetData(widget)) {
    if (!widgetOperations.isValidNoteWidgetData(widget)) {
      validationProblems = {
        widgetError: WidgetError.MISSING_NOTE_CONTENT,
      };
    }
  } else if (widgetOperations.isPowerBiIframeWidgetData(widget)) {
    if (!widget.url || !isAllowedPowerBiUrl(widget.url)) {
      validationProblems = {
        widgetError: WidgetError.INVALID_URL,
      };
    }
  } else {
    // NOTE: isChartWidgetData == false if missing viewType
    // NOTE: if a user can't edit a widget, skip validation so they can save changes to
    // widgets they can edit even if the other widgets had problems
    validationProblems =
      (widget.permissions?.canEditWidget ?? false)
        ? validateChartWidgetData({
            widget,
            availableDataSources,
            isIgnoreBackendError: false, // don't save if invalid widgets (error from running widget preview)
            isLoadingDataSources,
          })
        : null;
  }
  return {
    ...widget,
    validationProblems,
  };
};

export const getReferencedDataSourceId = (
  datasource?: APIChartWidgetAttributes['datasource']
): ArdoqId | undefined =>
  datasource && !isViewpointWidgetDatasource(datasource)
    ? datasource.sourceId
    : undefined;

const getDataSource = (
  availableDataSources: WidgetDataSourceEntity[],
  selectedDataSource?: APIChartWidgetAttributes['datasource']
) => {
  const sourceId = getReferencedDataSourceId(selectedDataSource);
  return availableDataSources?.find(
    dataSource => sourceId === dataSource.sourceId
  );
};

export const getAvailableFieldsForDataSource = (
  availableDataSources: WidgetDataSourceEntity[],
  selectedDataSource?: APIChartWidgetAttributes['datasource']
) =>
  getDataSource(availableDataSources, selectedDataSource)?.availableFields ??
  [];

export const getDataSourceStrategy = (
  availableDataSources: WidgetDataSourceEntity[],
  selectedDataSource?: APIChartWidgetAttributes['datasource']
) =>
  getDataSource(availableDataSources, selectedDataSource)?.datasourceStrategy;

export const hasAvailableDataSource = (
  availableDataSources: WidgetDataSourceEntity[],
  resourceId?: ArdoqId
) =>
  Boolean(
    resourceId &&
    availableDataSources.find(dataSource => resourceId === dataSource.sourceId)
      ?.availableFields
  );

export const getWidgetWarnings = (
  widgets: DashboardBuilderWidget[]
): WidgetWarning[] =>
  widgets
    .map(({ warning }) => warning)
    .filter((warning): warning is WidgetWarning => Boolean(warning));

export const getWidgetValidationProblems = (
  widgets: DashboardBuilderWidget[]
): WidgetValidationErrors[] =>
  widgets.map(w => w.validationProblems).filter(isTruthy) || [];

export const hasWidgetFieldValidationErrors = (
  widgets: DashboardBuilderWidget[]
) => widgets.filter(w => w.validationProblems?.fieldErrors).length > 0;

export function isSubset<T>(subset: T[], parent: T[]): boolean {
  return subset.every(el => parent.includes(el));
}

export const getSelectedFields = (
  selectedWidget: DashboardBuilderChartWidget,
  availableFields: DataSourceField[]
): DataSourceField[] =>
  selectedWidget.fields
    .map(field => availableFields.find(f => field === f.name))
    .filter(isTruthy);

export const isSortableColumInTableWidget = ({
  field,
  custom,
  type,
  referenceTypeIncoming,
  referenceTypeOutgoing,
}: DataSourceField) =>
  Boolean(
    (field || custom) &&
    isAPIFieldTypeSortable(type) &&
    !(referenceTypeIncoming || referenceTypeOutgoing)
  );

export const getDefaultOrderSelectionDirection = (
  orderSelection?: ChartOrderOptions
): SortOrder =>
  orderSelection && orderSelection !== ChartOrderOptions.SIZE
    ? SortOrder.ASC
    : SortOrder.DESC;

export const getSortOrder = (
  selectedColumn: string | undefined,
  selectedFields: DataSourceField[]
) =>
  selectedFields.find(field => field.name === selectedColumn)?.type ===
  APIFieldType.NUMBER
    ? SortOrder.DESC
    : SortOrder.ASC;

const getReferenceSelectOptionIcon = ({
  referenceTypeOutgoing,
  referenceTypeIncoming,
}: {
  referenceTypeOutgoing: DataSourceField['referenceTypeOutgoing'];
  referenceTypeIncoming: DataSourceField['referenceTypeIncoming'];
}): IconPropsForSelectOption['rightIcon'] => {
  if (!referenceTypeOutgoing && !referenceTypeIncoming) return undefined;
  return {
    name: referenceTypeOutgoing ? IconName.ARROW_FORWARD : IconName.ARROW_BACK,
  };
};

export const MISSING_FIELD_FROM_DATA_SOURCE = 'MissingFieldFromDataSource';
export const dataSourceFieldToSelectOption =
  (i18nLocale: I18nLocale) =>
  ({
    label,
    name,
    type,
    referenceTypeOutgoing,
    referenceTypeIncoming,
  }: DataSourceField): SelectOption<string> => ({
    label: `${label ?? name}${type ? ` (${type})` : ''}`,
    value: name,
    rightIcon: getReferenceSelectOptionIcon({
      referenceTypeIncoming,
      referenceTypeOutgoing,
    }),
    rightContent:
      name === MISSING_FIELD_FROM_DATA_SOURCE ? (
        <Tag
          statusType={StatusType.ERROR}
          label={t(i18nLocale, 'dashboard:builder.deletedField.tag', 'Deleted')}
          data-tooltip-text={t(
            i18nLocale,
            'dashboard:builder.deletedField.tooltip',
            'This field has been deleted from the report.\n    Clear it from your chart to save this dashboard.'
          )}
        />
      ) : null,
  });

export const dataSourceFieldToTypedSelectOption = (
  field: DataSourceField,
  permissionContext: PermissionContext,
  isSelected: boolean = false,
  i18nLocale: I18nLocale
): SelectOption<DataSourceField> => {
  const { label, name, type, referenceTypeOutgoing, referenceTypeIncoming } =
    field;
  const isDeletedField = name === MISSING_FIELD_FROM_DATA_SOURCE;

  const selectOptionBase = {
    label: `${label ?? name}${type ? ` (${type})` : ''}`,
    value: field,
    rightContent: isDeletedField ? (
      <Tag
        statusType={StatusType.ERROR}
        label={t(i18nLocale, 'dashboard:builder.deletedField.tag', 'Deleted')}
        data-tooltip-text={t(
          i18nLocale,
          'dashboard:builder.deletedField.tooltip',
          'This field has been deleted from the report.\n    Clear it from your chart to save this dashboard.'
        )}
      />
    ) : null,
    statusType: isDeletedField ? StatusType.ERROR : StatusType.DEFAULT,
  };

  if (fieldIsConfidential(permissionContext, { name, type })) {
    return toConfidentialFieldSelectOption(selectOptionBase, isSelected);
  }

  return {
    ...selectOptionBase,
    rightIcon: getReferenceSelectOptionIcon({
      referenceTypeIncoming,
      referenceTypeOutgoing,
    }),
  };
};
/**
 * Goal of this function is to check that an attribute is different between the old widget state
 * and the new widget state in a way that we should fetch a new preview
 */
export const shouldGetPreview = (
  newWidgetState: DashboardBuilderWidget | undefined,
  oldWidgetState: DashboardBuilderWidget | undefined,
  availableDataSources: WidgetDataSourceEntity[],
  isWidgetLinkedDashboardFiltersChanged: boolean,
  hasActiveGlobalFilters: boolean
): newWidgetState is DashboardBuilderChartWidget => {
  // if new widget is copied when there is active global filters, since dashboard filter bindings are not copied,
  // in case the source widget has applied global filters, needs to get preview for new widget
  if (
    hasActiveGlobalFilters &&
    !oldWidgetState &&
    newWidgetState &&
    widgetOperations.isChartWidgetData(newWidgetState) &&
    hasRequiredDataForPreview(newWidgetState, availableDataSources)
  ) {
    return true;
  }

  if (
    !(
      newWidgetState &&
      widgetOperations.isChartWidgetData(newWidgetState) &&
      hasRequiredDataForPreview(newWidgetState, availableDataSources) &&
      oldWidgetState &&
      widgetOperations.isChartWidgetData(oldWidgetState)
    )
  ) {
    return false;
  }
  if (newWidgetState.isLoading) return false; // don't fetch new preview if already loading one

  const isWidgetTypeChanged =
    newWidgetState.viewType !== oldWidgetState.viewType;

  const isDataSourceChanged =
    getReferencedDataSourceId(newWidgetState.datasource) !==
    getReferencedDataSourceId(oldWidgetState.datasource);

  const isAggregateChanged =
    newWidgetState.aggregate !== oldWidgetState.aggregate;

  const isLineChartPeriodChanged =
    widgetOperations.isTimeLineData(newWidgetState) &&
    widgetOperations.isTimeLineData(oldWidgetState) &&
    newWidgetState.period !== oldWidgetState.period;

  const shouldFetchMoreTableData =
    widgetOperations.isTableWidgetData(newWidgetState) &&
    newWidgetState.layout.h !== oldWidgetState.layout.h;

  const isTableSortOrOrderChanged =
    widgetOperations.isTableWidgetData(newWidgetState) &&
    widgetOperations.isTableWidgetData(oldWidgetState) &&
    (newWidgetState.sort !== oldWidgetState.sort ||
      newWidgetState.order !== oldWidgetState.order);

  const isChartOrderSelectionChanged =
    widgetOperations.hasConfigurableSlices(oldWidgetState) &&
    widgetOperations.hasConfigurableSlices(newWidgetState) &&
    (newWidgetState.orderSelection !== oldWidgetState.orderSelection ||
      newWidgetState.order !== oldWidgetState.order);

  const isTrendPercentageChanged =
    widgetOperations.isNumberWidgetData(oldWidgetState) &&
    widgetOperations.isNumberWidgetData(newWidgetState) &&
    !isEqual(
      pick(newWidgetState.trend, TREND_PROPS_THAT_SHOULD_TRIGGER_PREVIEW),
      pick(oldWidgetState.trend, TREND_PROPS_THAT_SHOULD_TRIGGER_PREVIEW)
    );

  const isConditionChanged =
    widgetOperations.isPercentageChartData(newWidgetState) &&
    !isEqual(
      newWidgetState.condition,
      widgetOperations.isPercentageChartData(oldWidgetState)
        ? oldWidgetState.condition
        : undefined
    );

  return Boolean(
    isWidgetTypeChanged ||
    shouldFetchMoreTableData ||
    !isEqual(newWidgetState.fields, oldWidgetState.fields) ||
    !isEqual(newWidgetState.filters, oldWidgetState.filters) ||
    isDataSourceChanged ||
    isAggregateChanged ||
    isLineChartPeriodChanged ||
    isTableSortOrOrderChanged ||
    isChartOrderSelectionChanged ||
    isTrendPercentageChanged ||
    isConditionChanged ||
    isWidgetLinkedDashboardFiltersChanged
  );
};

export const getIsWidgetLinkedDashboardFiltersChanged = (
  previousState: {
    dashboardFilterValues?: DashboardFilterValues;
    dashboardFilterParametersDict: Record<ArdoqId, APIDashboardFilterParameter>;
    widgetGlobalFilterBindings?: WidgetGlobalFilterBindings;
  },
  newState: {
    dashboardFilterValues?: DashboardFilterValues;
    dashboardFilterParametersDict: Record<ArdoqId, APIDashboardFilterParameter>;
    widgetGlobalFilterBindings?: WidgetGlobalFilterBindings;
  }
) => {
  const isBindingsToDashboardFiltersChanged = !isEqual(
    previousState.widgetGlobalFilterBindings,
    newState.widgetGlobalFilterBindings
  );
  if (isBindingsToDashboardFiltersChanged) {
    return true;
  }

  // If there are no linked parameters before and after, then nothing changed
  if (isEmpty(newState.widgetGlobalFilterBindings)) {
    return false;
  }

  // check if any of the linked parameters or the filter values have changed
  const linkedParameterIds = Object.values(
    newState.widgetGlobalFilterBindings || {}
  );

  const isAnyLinkedDashboardFilterParameterChanged = linkedParameterIds.some(
    parameterId =>
      !isEqual(
        previousState.dashboardFilterParametersDict[parameterId],
        newState.dashboardFilterParametersDict[parameterId]
      )
  );
  if (isAnyLinkedDashboardFilterParameterChanged) {
    return true;
  }

  const isAnyLinkedDashboardFilterValueChanged = linkedParameterIds.some(
    parameterId =>
      !isEqual(
        previousState.dashboardFilterValues?.[parameterId] || [],
        newState.dashboardFilterValues?.[parameterId] || []
      )
  );
  return isAnyLinkedDashboardFilterValueChanged;
};

/**
 *   Only use when we know there is a selected chart widget.
 *   Throws an error if there is no selected widget or if the selected widget is a header widget or note widget.
 *   Bubble charts are never edited in the builder, so the return type excludes them and `datasource` is always a referenced `WidgetDatasource`.
 */
export const getSelectedChartWidget = (
  widgets: DashboardBuilderWidget[],
  widgetId: string | null
): DashboardBuilderNonBubbleChartWidget => {
  const selectedWidget =
    widgetId && widgets.find(widget => widget._id === widgetId);
  if (
    !selectedWidget ||
    widgetOperations.isStaticContentWidgetData(selectedWidget)
  ) {
    throw new Error(
      !selectedWidget
        ? 'getSelectedChartWidget was called when no widget was selected'
        : 'getSelectedChartWidget was called when a header or note widget was selected'
    );
  }
  return selectedWidget as DashboardBuilderNonBubbleChartWidget;
};

export const DEFAULT_TREND_CONFIGURATION: NumberWidgetTrendConfiguration &
  RollingDateRange = {
  rangeType: NumberWidgetTrendRangeType.ROLLING,
  rolling: { value: 7, unit: 'day' },
  colorScheme: TrendColorScheme.CLASSIC,
} as const;

export const getNewNumberWidgetConfiguration = (
  trend: boolean | NumberWidgetTrend | NumberWidgetTrendConfiguration
): NumberWidgetTrend | NumberWidgetTrendConfiguration | undefined => {
  if (trend === true) return DEFAULT_TREND_CONFIGURATION;
  if (trend === false) return undefined;

  return trend;
};

const TREND_PROPS_THAT_SHOULD_TRIGGER_PREVIEW: Array<
  keyof (NumberWidgetTrend & RollingDateRange)
> = ['rangeType', 'rolling'] as const;

export const getNewNumberWidgetTrend = (
  selectedWidget: NumberWidgetConfiguration
): NumberWidgetTrendConfiguration | undefined => {
  if (
    selectedWidget.datasource.sourceType === WidgetDataSourceTypes.SURVEY &&
    !selectedWidget.fields.includes(SURVEY_VALID_INVALID_FIELD_NAME) // survey-based widget trends can only work with valid/invalid field)
  ) {
    return undefined;
  }

  return selectedWidget.trend;
};

const validateCategoricalChartData = (
  widget: DashboardBuilderChartWidget,
  availableDataSources: WidgetDataSourceEntity[],
  isLoadingDataSources: boolean
): WidgetValidationErrors | null => {
  const skipFieldTypeValidation = isLoadingDataSources; // not yet available
  const availableFields = isLoadingDataSources
    ? []
    : getAvailableFieldsForDataSource(availableDataSources, widget.datasource);
  const { aggregate, fields } = widget;
  if (!fields.length || fields.length > 2)
    return { widgetError: WidgetError.DEFAULT_ERROR }; // UI shouldn't let this happen
  const f1 = availableFields.find(f => f.name === widget.fields[0]);
  const f2 = availableFields.find(f => f.name === widget.fields[1]);
  const isField1Valid =
    skipFieldTypeValidation || (f1 && isValidLabelFieldForCategoricalChart(f1));

  let widgetError;
  const fieldErrors: WidgetValidationFieldErrors = {};
  if (fields.length === 1) {
    if (!isField1Valid) {
      // We've only a single Number field
      fieldErrors.labelField = LABEL_FIELD_MISSING;
    } else if (aggregate === Aggregate.SUM) {
      fieldErrors.valueField = VALUE_FIELD_MISSING;
    }
  } else if (fields.length === 2)
    if (
      !(
        (
          skipFieldTypeValidation ||
          Boolean(isField1Valid && f2?.type === APIFieldType.NUMBER)
        )
        // && aggregate === Aggregate.SUM - don't require so that users can switch count<>sum
        // and get preview; we drop the extra field on save / on preview
      )
    ) {
      widgetError = WidgetError.DEFAULT_ERROR; // UI shouldn't let this happen
    }

  const result = omitBy({ fieldErrors, widgetError }, isEmpty);

  return isEmpty(result) ? null : result;
};

export const validateChartWidgetData = ({
  widget,
  availableDataSources,
  isIgnoreBackendError = false,
  isLoadingDataSources,
}: {
  widget: DashboardBuilderChartWidget;
  availableDataSources: WidgetDataSourceEntity[];
  isIgnoreBackendError?: boolean;
  isLoadingDataSources: boolean;
}): WidgetValidationErrors | null => {
  const { datasource, error, errorCode, fields, viewType } = widget;

  const deletedDatasource = errorCode === WidgetErrorCode.DELETED_DATASOURCE;

  const isMissingDataSource = !datasource || deletedDatasource;

  const isMissingField =
    (widgetOperations.isTableWidgetData(widget) ||
      datasource?.sourceType === WidgetDataSourceTypes.SURVEY) &&
    !fields.length;

  const availableFields = getAvailableFieldsForDataSource(
    availableDataSources,
    datasource
  );

  const isNotSubsetOfAvailableFields =
    !widgetOperations.isBubbleChartData(widget) &&
    availableDataSources &&
    !isEmpty(availableDataSources) &&
    !isSubset(
      fields,
      availableFields.map(x => x.name)
    );

  const conditionColumnKey =
    widgetOperations.isPercentageChartData(widget) && !isEmpty(widget.condition)
      ? conditionOperations.apiFilterToCondition(widget.condition)?.columnKey
      : undefined;

  const isInvalidConditionColumn =
    !isLoadingDataSources &&
    !isEmpty(availableDataSources) &&
    Boolean(
      conditionColumnKey &&
      !availableFields.find(f => f.name === conditionColumnKey)
    );

  const backendError = isIgnoreBackendError ? undefined : error;

  let widgetError;
  if (isMissingDataSource) {
    if (!(deletedDatasource && backendError)) {
      // If BE error for 'deleted datasource then we only want to show that one
      widgetError = WidgetError.MISSING_CHART_SOURCE;
    }
  } else if (isMissingField) {
    widgetError = WidgetError.MISSING_FIELDS;
  } else if (isNotSubsetOfAvailableFields) {
    if (errorCode !== WidgetErrorCode.UNKNOWN_FIELD) {
      // Don't need the error twice
      // Does this actually ever happen that we have this and not the BE one?!
      widgetError = WidgetError.FIELD_REMOVED_FROM_SOURCE;
    }
  } else if (isInvalidConditionColumn) {
    widgetError = WidgetError.CONDITION_FIELD_REMOVED_FROM_SOURCE;
  } else if (!viewType) {
    // UI should prevent this from happening, but just in case...
    widgetError = WidgetError.DEFAULT_ERROR;
  }

  let frontendErrors: WidgetValidationErrors | null = null;
  if (widgetError) {
    frontendErrors = { widgetError };
  } else if (
    widgetOperations.isCategoricalChartData(widget) &&
    !(deletedDatasource && backendError)
  ) {
    frontendErrors = validateCategoricalChartData(
      widget,
      availableDataSources,
      isLoadingDataSources
    );
  }

  return frontendErrors || backendError
    ? {
        ...frontendErrors,
        backendError,
      }
    : null;
};

type RequiredKeys<T, K extends keyof T> = T & Required<Pick<T, K>>;

const hasRequiredDataForPreview = (
  widget: DashboardBuilderChartWidget,
  availableDataSources: WidgetDataSourceEntity[]
): widget is RequiredKeys<
  DashboardBuilderChartWidget,
  'datasource' | 'fields' | 'viewType'
> => {
  return !validateChartWidgetData({
    widget,
    availableDataSources,
    isIgnoreBackendError: true, // we want to retry, after a widget is updated
    isLoadingDataSources: false,
  });
};

export const getErrorMessage = (
  error: WidgetError | string,
  i18nLocale: I18nLocale
): string => {
  if (error === WidgetError.MISSING_CHART_SOURCE)
    return t(
      i18nLocale,
      'dashboard:builder.error.missingChartSource',
      'Data source missing! Select a report or a survey or delete this chart.'
    );
  if (error === WidgetError.MISSING_FIELDS)
    return t(
      i18nLocale,
      'dashboard:builder.error.missingFields',
      'Select a field to use this chart.'
    );
  if (error === WidgetError.EMPTY_WIDGET)
    return t(
      i18nLocale,
      'dashboard:builder.error.emptyWidget',
      'Select data to display from the sidebar menu on the right.'
    );
  if (error === WidgetError.MISSING_NOTE_CONTENT)
    return t(
      i18nLocale,
      'dashboard:builder.error.emptyNote',
      'Note can not be empty.'
    );
  if (error === WidgetError.CONDITION_FIELD_REMOVED_FROM_SOURCE)
    return t(
      i18nLocale,
      'dashboard:builder.condition.fieldErrorMessage',
      'There is an error with the condition. Clear the condition to save this dashboard.'
    );
  if (error === LABEL_FIELD_MISSING)
    return t(
      i18nLocale,
      'dashboard:builder.error.labelFieldMissing',
      'A label is required to group your data and save your chart'
    );
  if (error === VALUE_FIELD_MISSING)
    return t(
      i18nLocale,
      'dashboard:builder.error.valueFieldMissing',
      'You must select a value to save your chart when using the sum aggregation'
    );
  return error;
};

export const getMissingFieldErrorMessage = (
  missingFields: DataSourceField[],
  sourceType: WidgetDataSourceTypes | undefined,
  i18nLocale: I18nLocale
): string | undefined => {
  if (!isEmpty(missingFields)) {
    const sourceString = sourceType ?? 'source';
    if (missingFields.length > 1)
      return t(
        i18nLocale,
        'dashboard:builder.error.missingFieldsMulti',
        { sourceString },
        'Some fields have been deleted from the {{sourceString}}. Clear them from your chart to save this dashboard.'
      );
    return t(
      i18nLocale,
      'dashboard:builder.error.missingFieldSingle',
      {
        sourceString,
        fieldLabel: missingFields[0].label ?? missingFields[0].name,
      },
      'The field {{fieldLabel}} has been deleted from the {{sourceString}}. Clear it from your chart to save this dashboard.'
    );
  }
  return undefined;
};

export const getDashboardFilterParametersWithFieldLabels = ({
  availableDataSources,
  widgetsDatasourceIds,
  dashboardFilterParameters,
}: {
  availableDataSources: WidgetDataSourceEntity[];
  widgetsDatasourceIds: string[];
  dashboardFilterParameters: APIDashboardFilterParameter[];
}): DashboardFilterParameter[] => {
  const allDashboardFilterFieldsByKey = keyBy(
    availableDataSources
      .filter(ds => widgetsDatasourceIds.includes(ds.sourceId))
      .flatMap(ds => ds.availableFields ?? []),
    'name'
  );

  return dashboardFilterParameters.map((param): DashboardFilterParameter =>
    !param.fieldApiName
      ? {
          ...param,
          fieldApiName: undefined,
          fieldLabel: undefined,
          type: undefined,
        }
      : {
          ...param,
          fieldLabel:
            allDashboardFilterFieldsByKey[param.fieldApiName]?.label ??
            param.fieldApiName,
        }
  );
};

export const mapDataSourceFieldToDashboardFilterSupportedField = (
  dataSourceField: DataSourceField,
  fieldsByName: Record<string, MinimalFieldRepresentation>,
  supportedDashboardFilterTypes: SupportedDashboardFilterType[],
  datasourceStrategy?: DataSourceType
): DashboardFilterSupportedField | null => {
  const dashboardFilterFieldType = dataSourceField.referenceTypeIncoming
    ? ReportColumnType.REFERENCE_TYPE_INCOMING
    : dataSourceField.referenceTypeOutgoing
      ? ReportColumnType.REFERENCE_TYPE_OUTGOING
      : dataSourceField.type;

  if (
    isReportFieldSupportedForDashboardFilters(dashboardFilterFieldType, {
      supportedDashboardFilterTypes,
      fieldsByName,
      fieldKey: dataSourceField.name,
      datasourceStrategy,
    })
  ) {
    return {
      key: dataSourceField.name,
      type: dashboardFilterFieldType,
      custom: Boolean(dataSourceField.custom),
    } satisfies DashboardFilterSupportedField;
  }

  return null;
};

export const getNotificationAreaProps = (
  isAdminOrWriter: boolean,
  personalSettings: Partial<PersonalSettingMap>
): NewFeatureNotificationProps[] =>
  isAdminOrWriter
    ? [
        {
          show:
            !personalSettings[
              PersonalSetting.AUTOLINK_AND_DATE_DASHBOARD_FILTER_NOTIFICATION
            ] && !isAutolinkAndDateDashboardFilterNotificationExpired(),
          onClose: () =>
            dispatchAction(
              updatePersonalSetting({
                personalSetting:
                  PersonalSetting.AUTOLINK_AND_DATE_DASHBOARD_FILTER_NOTIFICATION,
                newValue: true,
              })
            ),
          customContent: <AutolinkAndDateDashboardFilterNotification />,
        },
      ]
    : [];

type StateToCompareInput = {
  _id?: ArdoqId;
  _version?: number;
  name?: string;
  description?: string;
  widgets: DashboardBuilderWidget[];
  settings: DashboardSettings;
  globalFilterParameters?: APIDashboardFilterParameter[];
  scenarioBranchIds?: Maybe<ArdoqId[]>;
};
export const getStateToCompare = (
  {
    _id,
    _version,
    name,
    description,
    widgets,
    settings,
    globalFilterParameters,
    scenarioBranchIds,
  }: StateToCompareInput,
  availableDataSources: WidgetDataSourceEntity[]
) => ({
  _id,
  _version,
  name,
  description,
  widgets: widgets
    ? [...widgets]
        .sort((a, b) => (a._id < b._id ? -1 : 1))
        .map(widget => ({
          ...(widgetOperations.isHeaderWidgetData(widget)
            ? formatHeaderWidget(widget)
            : widgetOperations.isNoteWidgetData(widget)
              ? formatNoteWidget(widget)
              : widgetOperations.isPowerBiIframeWidgetData(widget)
                ? formatPowerBiIframeWidget(widget)
                : formatChartWidget(
                    widget,
                    getAvailableFieldsForDataSource(
                      availableDataSources,
                      widget.datasource
                    )
                  )),
          // `formatChartWidget` drops field names not present in the
          // datasource, so a stale/missing field gets normalised away on
          // both sides of the diff. Keep the raw list here so that removing
          // such a field still registers as a change.
          ...(widgetOperations.isChartWidgetData(widget)
            ? { _rawFields: widget.fields }
            : {}),
        }))
    : [],
  settings,
  globalFilterParameters: globalFilterParameters?.map(p =>
    omit(p, 'fieldLabel')
  ),
  scenarioBranchIds,
});
